import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { MessageBanner, MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import { Text } from "@amzn/stencil-react-components/text";
import React, { FC } from "react";
import { getString } from "@/common/uistringlabels/ui-string-utils";
import { formatCampaignEscalationDeadline } from "../campaigns.util";
import { useCampaignResponseForm } from "../hooks/use-campaign-response-form.hook";
import { CampaignAcknowledgePanel } from "./campaign-acknowledge-panel";
import { CampaignInstructionBanner } from "./campaign-instruction-banner";
import { CampaignResponseTable } from "./campaign-response-table";
import { CampaignSubmissionBanners } from "./campaign-submission-banners";
import { PickupAddressForm } from "./pickup-address-form";
import { RETURN_PICKUP_RESPONSE } from "../response-options";
import type { MyStatus } from "@/services/hardware-self-service/hardware-self-service.types";
import type {
  Asset,
  MyStatusAssetResponse,
  CampaignSummary,
  ScopeSummary,
} from "@amzn/hardware-self-service-api-model";

interface CampaignResponseFormProps {
  campaign?: CampaignSummary;
  assets: Asset[];
  existingResponses: Record<string, MyStatusAssetResponse>;
  existingPickupAddress?: MyStatus["pickupAddress"];
  /** The participant the responses belong to — the manager's report when responding on behalf. */
  login: string;
  currentUsername: string;
  scope?: ScopeSummary;
  showRespondByDate?: boolean;
}

export const CampaignResponseForm: FC<CampaignResponseFormProps> = ({
  campaign,
  assets,
  existingResponses,
  existingPickupAddress,
  login,
  currentUsername,
  scope,
  showRespondByDate = true,
}) => {
  const form = useCampaignResponseForm({
    campaign,
    assets,
    existingResponses,
    existingPickupAddress,
    login,
  });

  if (!campaign) {
    return (
      <MessageBanner type={MessageBannerType.Informational} isDismissible>
        {getString("campaigns.noActiveCampaignForm")}
      </MessageBanner>
    );
  }

  const isOnBehalf = login !== currentUsername;
  const onBehalfOfLogin = isOnBehalf ? login : undefined;
  const isReadOnly = isOnBehalf && scope?.hasTargetSubmittedAResponse === true;
  const respondByDate = showRespondByDate ? formatCampaignEscalationDeadline(campaign) : undefined;

  if (assets.length === 0) {
    return (
      <CampaignAcknowledgePanel
        onBehalfOfLogin={onBehalfOfLogin}
        respondByDate={respondByDate}
        isSubmitting={form.isSubmitting}
        submitError={form.submitError}
        hasSubmitSucceeded={form.hasSubmitSucceeded}
        saveAttemptCount={form.saveAttemptCount}
        onAcknowledge={form.acknowledgeNoAssets}
      />
    );
  }

  const pickupDeviceNames = form.rows
    .filter((row) => form.responses[row.key] === RETURN_PICKUP_RESPONSE)
    .map((row) => row.asset.model ?? getString("campaigns.unknownDevice"));

  const showPickupForm = pickupDeviceNames.length > 0;

  return (
    <Col gridGap="S300">
      {isReadOnly ? (
        <MessageBanner type={MessageBannerType.Warning} isDismissible={false}>
          {getString("campaigns.delegatedLocked")}
        </MessageBanner>
      ) : (
        <CampaignInstructionBanner
          message={getString("campaigns.confirmDevices")}
          onBehalfOfLogin={onBehalfOfLogin}
          respondByDate={respondByDate}
        />
      )}

      <CampaignSubmissionBanners
        hasSubmitSucceeded={form.hasSubmitSucceeded}
        submitError={form.submitError}
        saveAttemptCount={form.saveAttemptCount}
      />

      <Text color="neutral70">
        {getString("campaigns.respondedCounter", {
          respondedCount: form.respondedCount,
          total: form.rows.length,
        })}
      </Text>

      <CampaignResponseTable
        rows={form.rows}
        responses={form.responses}
        remarks={form.remarks}
        rowsMissingRequiredRemarks={form.rowsMissingRequiredRemarks}
        isReadOnly={isReadOnly}
        onResponseChange={form.onResponseChange}
        onRemarksChange={form.onRemarksChange}
      />

      {showPickupForm && (
        <PickupAddressForm
          address={form.pickupAddress}
          deviceNames={pickupDeviceNames}
          onChange={form.onPickupAddressChange}
        />
      )}

      {!isReadOnly && (
        <Row justifyContent="flex-end">
          <Button
            variant={ButtonVariant.Primary}
            disabled={!form.isReadyToSave || form.isSubmitting}
            onClick={form.saveResponses}
            dataTestId="save-responses-button"
          >
            {form.isSubmitting
              ? getString("campaigns.saving")
              : getString("campaigns.saveResponses")}
          </Button>
        </Row>
      )}
    </Col>
  );
};
